import { tokens, IconButton, Slider, Tooltip } from '@/design';

export interface ProfileToolbarProps {
  readonly exaggeration: number;
  readonly onExaggerationChange: (value: number) => void;
  readonly onExport: () => void;
  readonly onAnnotate: () => void;
  readonly onCompare: () => void;
  readonly disabled?: boolean;
}

export function ProfileToolbar({
  exaggeration,
  onExaggerationChange,
  onExport,
  onAnnotate,
  onCompare,
  disabled = false,
}: ProfileToolbarProps) {
  return (
    <div
      role="toolbar"
      aria-label="Profile tools"
      aria-orientation="horizontal"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: tokens.space[2],
        height: tokens.layout.profileToolbarHeight,
        paddingInline: tokens.space[3],
        background: tokens.color.surface[1],
        borderBottom: tokens.border.thin,
      }}
    >
      <Tooltip content="Vertical exaggeration">
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: tokens.space[1],
            minWidth: 140,
          }}
        >
          <span className="material-symbols-outlined" aria-hidden="true">
            height
          </span>
          <Slider
            min={1}
            max={10}
            step={1}
            value={exaggeration}
            aria-label={`Exaggeration ${exaggeration}×`}
            onChange={onExaggerationChange}
            data-testid="profile-exaggeration-slider"
          />
          <span
            aria-live="polite"
            style={{
              fontFamily: tokens.font.family.mono,
              fontSize: tokens.font.size.xs,
              minWidth: 28,
            }}
          >
            {exaggeration}×
          </span>
        </span>
      </Tooltip>

      <Tooltip content="Compare profiles">
        <IconButton
          aria-label="Compare profiles"
          onClick={onCompare}
          disabled={disabled}
          data-testid="profile-compare-btn"
        >
          <span className="material-symbols-outlined">compare_arrows</span>
        </IconButton>
      </Tooltip>

      <Tooltip content="Add annotation">
        <IconButton
          aria-label="Add annotation"
          onClick={onAnnotate}
          disabled={disabled}
          data-testid="profile-annotate-btn"
        >
          <span className="material-symbols-outlined">comment</span>
        </IconButton>
      </Tooltip>

      <Tooltip content="Export profile">
        <IconButton
          aria-label="Export profile"
          onClick={onExport}
          disabled={disabled}
          data-testid="profile-export-btn"
        >
          <span className="material-symbols-outlined">ios_share</span>
        </IconButton>
      </Tooltip>
    </div>
  );
}
